import { createHmac, timingSafeEqual } from "node:crypto";

/**
 * Verify Meta's `signed_request`, delivered to the data-deletion callback.
 *
 * Meta App Review will not accept a submission without a deletion callback, so
 * this sits on the critical path to the DM export, which sits on the critical
 * path to everything in Part A.
 *
 * It is also the most dangerous endpoint in the product: it deletes customer
 * data on an unauthenticated inbound request. Every failure mode here fails
 * CLOSED, including a misconfigured secret, because an erasure endpoint that
 * verifies against an empty string is a delete-any-customer button.
 *
 * The secret is passed in by the caller, read from META_APP_SECRET. Only the
 * variable NAME appears in this repo.
 */

export type SignedRequestFailure =
  | "malformed"
  | "no_secret_configured"
  | "bad_signature"
  | "unsupported_algorithm"
  | "missing_user_id"
  | "stale";

export type SignedRequestResult =
  | { readonly ok: true; readonly userId: string; readonly issuedAt: Date }
  | { readonly ok: false; readonly reason: SignedRequestFailure };

/** Tolerated clock skew either side of `issued_at`. */
export const MAX_AGE_MS = 60 * 60 * 1000;

interface SignedPayload {
  algorithm?: unknown;
  user_id?: unknown;
  issued_at?: unknown;
}

export function parseSignedRequest(
  signedRequest: string,
  appSecret: string,
  now: Date,
): SignedRequestResult {
  // Fail closed on a missing secret. This is the misconfiguration that turns
  // the endpoint into an open door, and it is far more likely than an attack.
  if (appSecret.length === 0) return { ok: false, reason: "no_secret_configured" };

  const parts = signedRequest.split(".");
  if (parts.length !== 2) return { ok: false, reason: "malformed" };

  const [encodedSig, encodedPayload] = parts;
  if (!encodedSig || !encodedPayload) return { ok: false, reason: "malformed" };

  const expected = createHmac("sha256", appSecret).update(encodedPayload).digest();
  let provided: Buffer;
  try {
    provided = Buffer.from(encodedSig, "base64url");
  } catch {
    return { ok: false, reason: "malformed" };
  }

  // Length check first: timingSafeEqual throws on a length mismatch, and a
  // throw here would surface as a 500 rather than a clean rejection.
  if (provided.length !== expected.length) return { ok: false, reason: "bad_signature" };
  if (!timingSafeEqual(provided, expected)) return { ok: false, reason: "bad_signature" };

  let payload: SignedPayload;
  try {
    payload = JSON.parse(Buffer.from(encodedPayload, "base64url").toString("utf8")) as SignedPayload;
  } catch {
    return { ok: false, reason: "malformed" };
  }

  if (payload.algorithm !== "HMAC-SHA256") {
    return { ok: false, reason: "unsupported_algorithm" };
  }

  const userId = typeof payload.user_id === "string" ? payload.user_id : "";
  if (userId.length === 0) return { ok: false, reason: "missing_user_id" };

  // Replay protection. A captured callback should not be usable indefinitely,
  // and an erasure is not reversible.
  if (typeof payload.issued_at !== "number") return { ok: false, reason: "stale" };
  const issuedAt = new Date(payload.issued_at * 1000);
  if (Math.abs(now.getTime() - issuedAt.getTime()) > MAX_AGE_MS) {
    return { ok: false, reason: "stale" };
  }

  return { ok: true, userId, issuedAt };
}
